import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect } from 'react';
import { getLevelDataMaps } from './util.js';
import HierarchyList from './HierarchyList.js';
import styles from './HierarchySelector.module.scss.js';

var _excluded = ["data", "maxLevels", "onSelectionChange", "selections", "noRollup", "levelMetaData", "disabled", "singleSelect"];
/**
 * Hierarchy selector is used to display a tree of nodes where the user may select multiple options, including all or none.
 *
 * ### Content
 *
 * #### Data
 *
 * Data should be structured in the type HierarchySelectorItem[]
 *
 * ### Features
 *
 * #### Max Levels
 *
 * Default - 5
 *
 * Can optionally change the number of levels by passing in the `maxLevels` prop
 *
 * #### Selections
 *
 * Passing the array here in the type Filter format pre selects the data in the hierarchy
 *
 * #### Disabled
 *
 * Can optionally disable the view of selector. Works alongside selections to disable checkboxes
 *
 * #### Single select
 *
 * Make the selector as single select and should be used along with `noRollup` prop set as true to make it truly single select
 *
 * ### Interaction
 *
 * The Hierarchy Selector requires a callback to be provided to the `onSelectionChange` prop to handle interaction with checkboxes within the selector.
 *
 * ### Example
 *
 * ```tsx
 * export default () => {
 * const [hierarchySelections, setHierarchySelections] = useState<Filter[]>([]);
 * const maxLevels = 5;
 *
 * const levelMetaData = [
 *   { label: 'SBUs' },
 *   { label: 'Departments' },
 *   { label: 'Category Groups' },
 *   { label: 'Categories' },
 *   { label: 'Sub Categories' },
 * ];
 *
 * const updateHierarchySelections = (hierarchySelections) => {}
 *
 * return (
 *  <div>
 *      <HierarchySelector
 *      data={data}
 *      maxLevels={maxLevels}
 *      onSelectionChange={updateHierarchySelections}
 *      selections={hierarchySelections}
 *      noRollup={false}
 *      levelMetaData={levelMetaData}
 *      disabled={false}
 *      />
 *  </div>
 * );
 * };
 * ```
 *
 */

var HierarchySelector = function HierarchySelector(props) {
  var data = props.data,
    _props$maxLevels = props.maxLevels,
    maxLevels = _props$maxLevels === void 0 ? 5 : _props$maxLevels,
    onSelectionChange = props.onSelectionChange,
    selections = props.selections,
    _props$noRollup = props.noRollup,
    noRollup = _props$noRollup === void 0 ? false : _props$noRollup,
    levelMetaData = props.levelMetaData,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$singleSelect = props.singleSelect,
    singleSelect = _props$singleSelect === void 0 ? false : _props$singleSelect,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    levelDataMaps = _useState2[0],
    setLevelDataMaps = _useState2[1];

  //While selecting options at any level, need to make sure parent items of the group are all checked
  var handleParentItemsSelection = function handleParentItemsSelection(item) {
    var _levelDataMaps, _item$parentNestedId;
    if (item.level < 1) return;
    var parentItem = (_levelDataMaps = levelDataMaps[item.level - 1]) === null || _levelDataMaps === void 0 ? void 0 : _levelDataMaps.get((_item$parentNestedId = item.parentNestedId) !== null && _item$parentNestedId !== void 0 ? _item$parentNestedId : '');
    if (!parentItem) return;
    if (!noRollup) {
      var selectedItems = parentItem.subHierarchy.filter(function (subItem) {
        return subItem.checked;
      });
      parentItem.checked = (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) > 0;
      parentItem.indeterminate = item.indeterminate || selectedItems.length > 0 && selectedItems.length < parentItem.subHierarchy.length;
    }
    parentItem.expanded = parentItem.indeterminate || parentItem.expanded || noRollup;
    parentItem.showChildren = parentItem.showChildren || item.checked || noRollup;
    handleParentItemsSelection(parentItem);
  };

  //While selecting options at any level, need to make sure child items of the group are all checked
  var handleChildItemsSelection = function handleChildItemsSelection(currentItem) {
    var shouldExpand = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    currentItem.subHierarchy.forEach(function (childItem) {
      if (!noRollup) {
        childItem.checked = currentItem.checked;
        childItem.indeterminate = currentItem.checked && childItem.indeterminate;
      }
      childItem.expanded = currentItem.expanded ? childItem.expanded && shouldExpand : currentItem.expanded;
      handleChildItemsSelection(childItem);
    });
  };
  var updateChildrenSelections = function updateChildrenSelections(item, currentSelections) {
    if (!item.indeterminate) return;
    item.subHierarchy.forEach(function (childItem) {
      if (childItem.checked) {
        if (!childItem.indeterminate) {
          currentSelections.push(_objectSpread2({
            nestedId: childItem.nestedId,
            parentNestedId: childItem.parentNestedId,
            parentId: childItem.parentId,
            name: childItem.name,
            level: childItem.level
          }, childItem.upcCount !== undefined ? {
            upcCount: childItem.upcCount
          } : {}));
        }
        updateChildrenSelections(childItem, currentSelections);
      }
    });
  };
  var getSelections = function getSelections(dataMaps) {
    var currentSelections = [];
    if (noRollup) {
      dataMaps.forEach(function (dataMap) {
        var items = Array.from(dataMap.values());
        items.forEach(function (item) {
          if (item.checked) {
            if (singleSelect) {
              currentSelections.splice(0, currentSelections.length);
            }
            currentSelections.push(_objectSpread2({
              nestedId: item.nestedId,
              name: item.name,
              level: item.level
            }, item.upcCount !== undefined ? {
              upcCount: item.upcCount
            } : {}));
          }
        });
      });
    } else {
      var level0Items = Array.from(dataMaps[0].values());
      level0Items.forEach(function (item) {
        if (item.checked) {
          if (!item.indeterminate) {
            currentSelections.push(_objectSpread2({
              nestedId: item.nestedId,
              name: item.name,
              level: item.level
            }, item.upcCount !== undefined ? {
              upcCount: item.upcCount
            } : {}));
          }
          updateChildrenSelections(item, currentSelections);
        }
      });
    }
    return currentSelections;
  };

  //To handle selection of option at any level, and handle child and parent options
  var handleSelectionChange = function handleSelectionChange(level, itemNestedId, checked, topLevel) {
    var _newLevelDataMaps$lev;
    var newLevelDataMaps = _toConsumableArray(levelDataMaps);
    if (singleSelect) {
      newLevelDataMaps.forEach(function (dataMap) {
        var items = Array.from(dataMap.values());
        items.forEach(function (item) {
          item.checked = false;
          item.expanded = false;
        });
      });
    }
    var currentItem = (_newLevelDataMaps$lev = newLevelDataMaps[level]) === null || _newLevelDataMaps$lev === void 0 ? void 0 : _newLevelDataMaps$lev.get(itemNestedId);
    if (!currentItem || currentItem.disabled) return;
    currentItem.checked = checked;
    currentItem.expanded = checked || noRollup && currentItem.expanded;
    currentItem.showChildren = checked || noRollup && currentItem.showChildren;
    currentItem.indeterminate = false;
    // All children would be either checked or NOT so no question of  indeterminate

    handleChildItemsSelection(currentItem, true);
    handleParentItemsSelection(currentItem);
    setLevelDataMaps(newLevelDataMaps);
    if (Number(level) === Number(topLevel)) {
      onSelectionChange(getSelections(newLevelDataMaps));
    }
  };
  var handleOnExpandChildren = function handleOnExpandChildren(currentItem) {
    currentItem.subHierarchy.forEach(function (childItem) {
      childItem.expanded = currentItem.expanded ? childItem.expanded : currentItem.expanded;
      // UnClose child and their child when parent is unexpanded
      if (!currentItem.expanded) {
        handleOnExpandChildren(childItem);
      }
    });
  };

  //To handle expand group in all levels
  var handleOnExpand = function handleOnExpand(itemLevel, itemNestedId) {
    var _newLevelDataMaps$ite;
    var newLevelDataMaps = _toConsumableArray(levelDataMaps);
    var currentItem = (_newLevelDataMaps$ite = newLevelDataMaps[itemLevel]) === null || _newLevelDataMaps$ite === void 0 ? void 0 : _newLevelDataMaps$ite.get(itemNestedId);
    if (!currentItem) return;
    var isExpanded = !currentItem.expanded;
    if (isExpanded) {
      // Collapse and hide siblings
      newLevelDataMaps[currentItem.level].forEach(function (sibling) {
        sibling.expanded = sibling.checked;
        sibling.showChildren = false;
      });
    }
    currentItem.expanded = isExpanded;
    currentItem.showChildren = currentItem.expanded;
    handleOnExpandChildren(currentItem);
    setLevelDataMaps(newLevelDataMaps);
  };

  //To handle select and deselect of all options at a given level
  var handleSelectAll = function handleSelectAll(level, checked, topLevel) {
    if (level < 1) {
      Array.from(levelDataMaps[level].values()).forEach(function (item) {
        handleSelectionChange(item.level, item.nestedId, checked, topLevel);
      });
    } else {
      var parentLevelExpandedItems = Array.from(levelDataMaps[level - 1].values()).filter(function (item) {
        return item.expanded;
      });
      parentLevelExpandedItems.forEach(function (pItem) {
        pItem.subHierarchy.forEach(function (childItem) {
          handleSelectionChange(childItem.level, childItem.nestedId, checked, level);
        });
      });
    }
  };
  var handleSubHierarchyExpandCollapse = function handleSubHierarchyExpandCollapse(parentLevel, parentNestedId) {
    var _newLevelDataMaps$par;
    var newLevelDataMaps = _toConsumableArray(levelDataMaps);
    var currentParentItem = (_newLevelDataMaps$par = newLevelDataMaps[parentLevel]) === null || _newLevelDataMaps$par === void 0 ? void 0 : _newLevelDataMaps$par.get(parentNestedId);
    if (!currentParentItem) return;
    currentParentItem.showChildren = !currentParentItem.showChildren;
    setLevelDataMaps(newLevelDataMaps);
  };

  //If selected options are present, restore them
  var restoreOriginalSelections = function restoreOriginalSelections() {
    if (!levelDataMaps || !(selections !== null && selections !== void 0 && selections.length)) return;
    selections.forEach(function (sel) {
      var _levelDataMaps$level;
      var level = sel.level,
        nestedId = sel.nestedId;
      if (level === undefined) return;
      var item = (_levelDataMaps$level = levelDataMaps[level]) === null || _levelDataMaps$level === void 0 ? void 0 : _levelDataMaps$level.get(nestedId);
      if ((item === null || item === void 0 ? void 0 : item.checked) === undefined) {
        handleSelectionChange(level, nestedId, true);
      }
    });
  };

  //Restructure the data onto array of Maps by level
  useEffect(function () {
    var newLevelDataMaps = getLevelDataMaps(data, []);
    setLevelDataMaps(newLevelDataMaps !== null && newLevelDataMaps !== void 0 ? newLevelDataMaps : []);
    restoreOriginalSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(selections)]);

  //Once levelData is created, restore original selections
  useEffect(restoreOriginalSelections,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [levelDataMaps]);
  var renderLevelDataMap = function renderLevelDataMap(topLevel) {
    return /*#__PURE__*/React__default.createElement(HierarchyList, {
      levelDataMaps: levelDataMaps,
      level: topLevel,
      header: levelMetaData[topLevel].label,
      onSelectAll: function onSelectAll(target, checked) {
        return handleSelectAll(target, checked, topLevel);
      },
      onSelectionChange: function onSelectionChange(level, itemNestedId, checked) {
        handleSelectionChange(level, itemNestedId, checked, topLevel);
      },
      onExpand: handleOnExpand,
      onSubHierarchyExpandCollapse: handleSubHierarchyExpandCollapse,
      disabled: disabled,
      singleSelect: singleSelect
    });
  };
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: styles.mapsContainer
  }, rest), (levelDataMaps || []).slice(0, maxLevels).map(function (_levelDataMap, level) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: styles.mapContainer
      // eslint-disable-next-line react/no-array-index-key
      ,
      key: "level-data-container-".concat(level)
    }, renderLevelDataMap(level));
  }));
};
HierarchySelector.displayName = 'HierarchySelector';

export { HierarchySelector };
